import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {SEARCH_NEWS_REQUEST_DATA} from "../../store/news/actions/search-news-actions";
import {resetCreateNewsData} from "../../store/news/actions/create-news-actions";
import {updateNewsRequest} from "../../store/news/actions/update-news-actions";
import NewsCreateForm from "../../components/NewsCreateForm";
import './News.scss';
import {deleteNewsRequest, RESET_DELETE_NEWS_STATE} from "../../store/news/actions/delete-news-actions";
import {toast} from "react-toastify";
import {SEARCH_CUSTOMERS_REQUEST_DATA} from "../../store/customer/actions/search-customers-actions";


const News = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [showForm, setShowForm] = useState(false);
    const [editNews, setEditNews] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);
    const [isSuccess, setSuccess] = useState(false);
    const [editedFields, setEditedFields] = useState({});

    const {data: newsData} = useSelector((state) => state.searchNewsData);
    const {response: updateNewsData} = useSelector((state) => state.updateNewsData);
    const {isDeleted} = useSelector((state) => state.deleteNews)

    useEffect(() => {
        dispatch({
            type: SEARCH_NEWS_REQUEST_DATA,
            payload: {params: {page: currentPage, size: 9}},
        });
    }, [dispatch, currentPage, isSuccess]);

    useEffect(() => {
        if (updateNewsData) {
            setSuccess(true);
        }
    }, [updateNewsData]);

    const onSuccess = () => {
        setSuccess(true);
        setShowForm(false);
        setEditNews(null);
        dispatch(resetCreateNewsData());
    };

    const onOpen = () => {
        setSuccess(false);
        setShowForm(true);
    };

    const onClose = () => {
        setSuccess(false);
        setShowForm(false);
        setEditNews(null);
        setEditedFields({});
    };

    const handlePreviousPage = () => {
        if (currentPage > 0) setCurrentPage(currentPage - 1);
    };

    const handleNextPage = () => {
        if (currentPage < newsData.totalPages - 1) setCurrentPage(currentPage + 1);
    };

    const handleFieldChange = (field, value) => {
        setEditedFields((prev) => ({...prev, [field]: {value}}));
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setEditedFields((prev) => ({...prev, newMainImage: {value: file}}));
        }
    };

    const handleSaveEdit = (id) => {
        const formData = new FormData();
        for (const key in editedFields) {
            const field = editedFields[key];
            if (field?.value !== undefined) {
                if (field.value instanceof File) {
                    formData.append(key, field.value);
                } else {
                    formData.append(key, field.value);
                }
            }
        }
        dispatch(updateNewsRequest(id, formData));
        onClose();
    };

    useEffect(() => {
        if (isDeleted) {
            toast.success(t('news.deleteNews') + ' ' + t('common.success'))
            dispatch({
                type: SEARCH_NEWS_REQUEST_DATA,
                payload: {params: {page: currentPage, size: 9}},
            });


            // Reset after small delay or immediately
            setTimeout(() => {
                dispatch({ type: RESET_DELETE_NEWS_STATE });
            }, 300); // enough to allow render updates
        }
    }, [isDeleted, t]);

    return (
        <div className="container mx-auto relative px-4">
            <div className="pt-10 min-h-screen flex flex-col">
                <div className="flex-grow">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {newsData?.value?.map((news) => (
                            <div key={news.id} className="bg-white shadow rounded-lg overflow-hidden relative">
                                <img src={news.src} alt={news.heading} className="h-48 w-full object-cover"/>
                                {editNews === news.id ? (
                                    <div className="absolute top-2 right-2">
                                        <input
                                            type="checkbox"
                                            checked={editedFields.published?.value ?? news.published}
                                            onChange={(e) => handleFieldChange("published", e.target.checked)}
                                        />
                                    </div>
                                ) : (
                                    news.published && (
                                        <div className="absolute top-2 right-2 text-green-500 text-xl">✔️</div>
                                    )
                                )}

                                <div className="p-4 space-y-2">
                                    {editNews === news.id ? (
                                        <>
                                            <input
                                                type="text"
                                                defaultValue={news.heading}
                                                className="w-full p-2 border rounded text-sm"
                                                onChange={(e) => handleFieldChange("heading", e.target.value)}
                                            />
                                            <textarea
                                                defaultValue={news.text}
                                                className="w-full p-2 border rounded text-sm resize-y"
                                                onChange={(e) => handleFieldChange("text", e.target.value)}
                                            />
                                            <input
                                                type="text"
                                                defaultValue={news.url}
                                                className="w-full p-2 border rounded text-sm"
                                                onChange={(e) => handleFieldChange("url", e.target.value)}
                                            />
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleFileChange}
                                                className="w-full text-sm border border-gray-300 rounded p-1 bg-white"
                                            />
                                            <button
                                                className="bg-green-600 hover:bg-green-700 text-white py-1 px-3 rounded text-sm"
                                                onClick={() => handleSaveEdit(news.id)}
                                            >
                                                Save
                                            </button>
                                            <button
                                                className="ml-2 text-gray-500 hover:text-gray-800 text-sm"
                                                onClick={onClose}
                                            >
                                                Cancel
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <h2 className="text-lg font-bold text-blue-700">
                                                {news.url ? (
                                                    <a href={news.url} target="_blank" rel="noopener noreferrer">
                                                        {news.heading}
                                                    </a>
                                                ) : (
                                                    news.heading
                                                )}
                                            </h2>
                                            <p className="text-gray-600 text-sm">{news.text}</p>
                                            <div className="flex justify-between items-center">
                                                <button
                                                    className="text-blue-500 text-sm hover:underline"
                                                    onClick={() => setEditNews(news.id)}
                                                >
                                                    Edit
                                                </button>
                                                <button onClick={() => dispatch(deleteNewsRequest(news.id))}
                                                        className="text-red-500">🗑️
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Pagination */}
                {newsData?.totalPages > 1 && (
                    <div className="mt-4 py-4 border-t flex justify-between items-center px-4 bg-white">
                        <button
                            onClick={handlePreviousPage}
                            disabled={currentPage <= 0}
                            className="bg-[#34495e] hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                        >
                            {t("pagination.previous")}
                        </button>
                        <span>
              {t("pagination.page")} {currentPage + 1} {t("pagination.of")} {newsData.totalPages}
            </span>
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage >= newsData.totalPages - 1}
                            className="bg-[#34495e] hover:bg-[#46637f] text-white font-bold py-2 px-4 rounded"
                        >
                            {t("pagination.next")}
                        </button>
                    </div>
                )}
            </div>

            {/* Floating Create Button */}
            <button
                onClick={onOpen}
                className="fixed bottom-6rem right-8 bg-[#34495e] hover:bg-[#46637f] text-white text-3xl rounded-full w-14 h-14 flex items-center justify-center shadow-lg"
                title="Create News"
            >
                +
            </button>

            {/* Create Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
                    <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg">Add news</h3>
                            <button onClick={onClose} className="text-gray-400 hover:bg-gray-200 p-2 rounded">
                                X
                            </button>
                        </div>
                        <NewsCreateForm onSuccess={onSuccess}/>
                    </div>
                </div>
            )}
        </div>
    );
};

export default News;
